import { useEffect, useState } from "react";
import deleteIcon from "../../../../assets/svg/deleteIcon.svg";
import completedRide from "../../../../assets/svg/completedRide.svg";
import ButtonEdit from "../../../../assets/svg/ButtonEdit.svg";
import block from "../../../../assets/svg/block.svg";
import "./ColumnsTable.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateDriver } from "services/customAPI";
import { toast } from "react-toastify";
import {
  DriverTableData,
  DriverTableProps,
  ApiError
} from "../../../../types/driver-table.types";
import { Types } from "shared-lib";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { FaCaretUp, FaCaretDown, FaEye } from "react-icons/fa";
import Card from "../../../../components/card";

import Select from "react-select";

const columnHelper = createColumnHelper<DriverTableData>();

function ColumnsTable(props: DriverTableProps) {
  const { tableData, handleClickForDeleteModal, handleViewDriver, statusOptions, setDriverStatus, driverStatus } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<DriverTableData[]>([...tableData]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleApprove = async (id: string): Promise<boolean> => {
    try {
      const driverApproved = await updateDriver(Number(id), {
        approved: Types.ApprovalStatus.APPROVED,
      });
      if (driverApproved) {
        toast.success(t("Driver approved successfully"));
        setData(
          data.map((item) =>
            item.action.id === id
              ? { ...item, action: { ...item.action, approved: Types.ApprovalStatus.APPROVED } }
              : item
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("error", apiError.message);
      toast.error(t(`${apiError.message}`));
      throw error;
    }
  };

  const handleClickForRejectDoc = async (id: string): Promise<boolean> => {
    try {
      const driverRejected = await updateDriver(Number(id), {
        approved: Types.ApprovalStatus.REJECTED,
      });
      if (driverRejected) {
        toast.success(t("Driver rejected successfully"));
        setData(
          data.map((item) =>
            item.action.id === id
              ? { ...item, action: { ...item.action, approved: Types.ApprovalStatus.REJECTED } }
              : item
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("error", apiError.message);
      toast.error(`${apiError.message}`);
      throw error;
    }
  };

  const columns = [
    columnHelper.accessor("fullName", {
      id: "fullName",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Full Name")}
        </p>
      ),
      cell: (info) => {
        const fullNameObj = info.getValue();
        return (
          <div className="flex items-center">
            {fullNameObj.path === "" ? (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <span className="text-sm font-bold text-gray-400">
                  {fullNameObj.name.charAt(0).toUpperCase()}
                </span>
              </div>
            ) : (
              <img
                src={fullNameObj.path}
                alt="Profile"
                className="w-10 h-10 rounded-full mr-3"
              />
            )}
            <span className="text-sm font-bold text-white">
              {fullNameObj.name}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("mobileNumber", {
      id: "mobileNumber",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Mobile Number")}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("vehicleNumber", {
      id: "vehicleNumber",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Vehicle")}
        </p>
      ),
      cell: (info) => {
        const row = info.row.original;
        const hasVehicle = row.vehicleNumber && row.vehicleNumber !== "N/A";
        const plate = info.getValue()?.trim() === "" ? "N/A" : info.getValue();

        if (!hasVehicle) {
          return <p className="text-sm font-bold text-gray-400">N/A</p>;
        }

        return (
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              if (row.vehicleId) {
                navigate(`/admin/vehicles/vehicleform/${row.vehicleId}`);
              }
            }}
            title={t("Ver veículo")}
            style={{ transition: "opacity 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {row.vehicleImageUrl ? (
              <img
                src={row.vehicleImageUrl}
                alt="Vehicle"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  objectFit: "cover",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              />
            ) : (
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#a0aec0",
                }}
              >
                🚗
              </div>
            )}
            <span className="text-sm font-bold text-white" style={{ textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}>
              {plate}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("vehicleType", {
      id: "vehicleType",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Vehicle Type")}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()?.name ? info.getValue().name : "N/D"}
        </p>
      ),
    }),
    columnHelper.accessor("action", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Status")}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p
            className={
              info.getValue().driverStatus === "active"
                ? "onlineClass text-sm font-bold"
                : info.getValue().driverStatus === "inactive"
                  ? "offlineClass text-sm font-bold"
                  : "onrideClass text-sm font-bold"
            }
          >
            {info.getValue().driverStatus === "active"
              ? t("Active")
              : t("Inactive")}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("action", {
      id: "approval",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("approve")}
        </p>
      ),
      cell: (info) => {
        const driver = info.getValue();
        const isApproved = driver?.approved === Types.ApprovalStatus.APPROVED;
        const isRejected = driver?.approved === Types.ApprovalStatus.REJECTED;
        const isPending = driver?.approved === Types.ApprovalStatus.PENDING;

        return (
          <div className="flex items-center">
            {isPending && (
              <>
                <div className="cursor-pointer">
                  <img
                    src={completedRide}
                    style={{ marginRight: "8px" }}
                    onClick={() => handleApprove(driver?.id)}
                    alt="Approve"
                  />
                </div>
                <div className="cursor-pointer">
                  <img
                    src={block}
                    onClick={() => handleClickForRejectDoc(driver?.id)}
                    alt="Reject"
                  />
                </div>
              </>
            )}
            {isApproved && (
              <span className="font-bold text-green-500">{t("Approved")}</span>
            )}
            {isRejected && (
              <span className="font-bold text-red-500">{t("Rejected")}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("action", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Action")}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-2">
          <div
            className="cursor-pointer"
            title={t("Visualizar")}
            onClick={() => handleViewDriver?.(info.row.original)}
          >
            <FaEye size={18} color="#a0aec0" />
          </div>
          <div className="cursor-pointer" title={t("Editar")}>
            <img
              src={ButtonEdit}
              style={{ marginRight: "8px" }}
              onClick={() =>
                navigate(`/admin/drivers/driverform/${info.getValue()?.id}`)
              }
              alt="Edit"
            />
          </div>
          <div className="cursor-pointer">
            <img
              src={deleteIcon}
              onClick={() => handleClickForDeleteModal(info.row.original)}
              alt="Delete"
            />
          </div>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-white">
          {t("Drivers")}
        </div>
        {statusOptions && setDriverStatus && (
          <div className="flex gap-4 items-center">
            <Select
              className="w-48 text-sm outline-none"
              options={statusOptions}
              value={statusOptions.find((opt) => opt.value === driverStatus)}
              onChange={(option) => {
                if (option && setDriverStatus) {
                  setDriverStatus(option.value);
                }
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: "#1e1e2d",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }),
                option: (base, { isFocused, isSelected }) => ({
                  ...base,
                  backgroundColor: isSelected
                    ? "rgba(66, 153, 225, 0.2)"
                    : isFocused
                      ? "rgba(255, 255, 255, 0.05)"
                      : "transparent",
                  color: isSelected ? "#63b3ed" : "white",
                  cursor: "pointer",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: "white",
                }),
                input: (base) => ({
                  ...base,
                  color: "white",
                }),
              }}
            />
          </div>
        )}
      </header>

      <div className="mt-4 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-white/10">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer pb-3 pr-4 pt-4 text-start"
                  >
                    <div className="flex gap-4 text-left text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() === "asc" ? (
                        <FaCaretUp
                          className="mr-[-6] font-bold text-gray-600"
                          size={20}
                        />
                      ) : header.column.getIsSorted() === "desc" ? (
                        <FaCaretDown
                          size={20}
                          className="font-bold text-gray-600"
                        />
                      ) : (
                        <div className="mr-[-6] flex">
                          <FaCaretDown
                            size={20}
                            className="font-bold text-gray-600"
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {tableData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  <h2 className="m-4 text-gray-400" style={{ fontSize: "30px" }}>
                    {t("No Results!")}
                  </h2>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table
                .getRowModel()
                .rows?.slice(0, 10)
                .map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`transition-colors hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                      }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="min-w-[135px] border-b border-white/5 py-3 pr-4 text-start"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          )}
        </table>
      </div>
    </Card>
  );
}

export default ColumnsTable;
